import React, { useState } from "react";
import { Button, Divider, Modal, Segmented, Space, Typography } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import SectionCard from "../../../../components/shared/SectionCard";
import { updateCustomerAdvanced } from "../../../../api/inventory/customerApi";
import { promoteUserToPatient } from "../../../../api/admin/userApi";
import { useNotifications } from "../../../../context/NotificationContext";
import { isCustomerAccountRow, isPatientDirectoryRow } from "../../../../types/inventory/customerRecord";
import { Customer } from "../../../../types/inventory/CustomerTypes";

const { Text } = Typography;

/**
 * CU-86d45b54n — the operator-facing Basic / Advanced control over the persisted customer_advanced
 * capability. The presentation follows the server value only; a request in flight changes nothing.
 * CU-14ygtkj7wuy — also presents the current Account Type with the one-way Convert to Patient action.
 */
const CUSTOMER_LEVEL_BASIC = "basic";
const CUSTOMER_LEVEL_ADVANCED = "advanced";

export const PENDING_CHANGES_BLOCK_MESSAGE =
	"Save or refresh the pending customer changes before converting to Patient.";

interface CustomerLevelSectionProps {
	customer: Customer;
	onCustomerChange: (customer: Customer) => void;
	/** CU-14ygtkj7wuy — true while the drawer holds unsaved profile edits; blocks the conversion. */
	hasUnsavedChanges?: boolean;
}

const responseMessage = (error: unknown): string | null => {
	const message = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;

	return typeof message === "string" && message.trim() ? message : null;
};

const failureMessage = (error: unknown): string => {
	const message = responseMessage(error);

	return message ? `Failed to update the customer level: ${message}` : "Failed to update the customer level.";
};

const conversionFailureMessage = (error: unknown): string =>
	responseMessage(error) ?? "Failed to convert the customer to a patient.";

const accountTypeLabel = (customer: Customer): string => {
	if (isCustomerAccountRow(customer)) return "Customer";
	if (isPatientDirectoryRow(customer)) return "Patient";

	return customer.user_type || "—";
};

const CustomerLevelSection: React.FC<CustomerLevelSectionProps> = ({
	customer,
	onCustomerChange,
	hasUnsavedChanges = false,
}) => {
	const [saving, setSaving] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [converting, setConverting] = useState(false);
	const { showSuccess, showError } = useNotifications();

	const persistedAdvanced = customer.customer_advanced === true;
	const value = persistedAdvanced ? CUSTOMER_LEVEL_ADVANCED : CUSTOMER_LEVEL_BASIC;
	const convertible = isCustomerAccountRow(customer);

	const handleChange = async (next: string) => {
		const requested = next === CUSTOMER_LEVEL_ADVANCED;

		if (requested === persistedAdvanced || saving || converting) return;

		setSaving(true);
		try {
			const updated = await updateCustomerAdvanced(customer.id, requested);
			onCustomerChange({ ...customer, ...updated });
			showSuccess(`Customer level updated to ${requested ? "Advanced" : "Basic"}`);
		} catch (error) {
			showError(failureMessage(error));
		} finally {
			setSaving(false);
		}
	};

	const convertToPatient = async () => {
		if (converting) return;

		setConverting(true);
		try {
			const promoted = await promoteUserToPatient(customer.id);
			setConfirmOpen(false);
			onCustomerChange({ ...customer, ...promoted });
			showSuccess("Customer converted to Patient");
		} catch (error) {
			showError(conversionFailureMessage(error));
		} finally {
			setConverting(false);
		}
	};

	return (
		<SectionCard title="Customer Level">
			<Space direction="vertical" size={4}>
				<Segmented
					value={value}
					disabled={saving || converting}
					onChange={(next) => void handleChange(String(next))}
					options={[
						{ label: "Basic", value: CUSTOMER_LEVEL_BASIC },
						{ label: "Advanced", value: CUSTOMER_LEVEL_ADVANCED },
					]}
					data-testid="customer-level-control"
				/>
				<Text type="secondary" style={{ fontSize: 11 }}>
					{saving ? (
						<>
							<LoadingOutlined /> Saving…
						</>
					) : (
						"Advanced marks eligibility for future advanced customer products. It does not change appointments, billing, orders or notifications."
					)}
				</Text>

				<Divider style={{ margin: "8px 0" }} />

				<Space size={8} wrap>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Account Type
					</Text>
					<Text strong data-testid="customer-account-type">
						{accountTypeLabel(customer)}
					</Text>
					{convertible && (
						<Button
							size="small"
							onClick={() => setConfirmOpen(true)}
							disabled={hasUnsavedChanges || saving || converting}
							loading={converting}
						>
							Convert to Patient
						</Button>
					)}
				</Space>
				{convertible && hasUnsavedChanges && (
					<Text type="warning" style={{ fontSize: 11 }}>
						{PENDING_CHANGES_BLOCK_MESSAGE}
					</Text>
				)}
			</Space>

			{convertible && (
				<Modal
					open={confirmOpen}
					title="Convert to Patient"
					okText="Confirm conversion"
					cancelText="Cancel"
					onOk={() => void convertToPatient()}
					onCancel={() => setConfirmOpen(false)}
					confirmLoading={converting}
					closable={!converting}
					maskClosable={false}
					destroyOnClose
				>
					<ul style={{ paddingLeft: 18, margin: 0 }}>
						<li>This is a one-way conversion in this screen: the account cannot be changed back to a Customer here.</li>
						<li>The account remains a member of the customer directory.</li>
						<li>The Basic / Advanced customer level is retained.</li>
						<li>No IHI or clinical data is created by the conversion.</li>
					</ul>
				</Modal>
			)}
		</SectionCard>
	);
};

export default CustomerLevelSection;
